import axios from "axios";

/**
 * Inactivity timeout helpers shared by the interceptor and InactivityGuard.
 *
 * The server owns the rule (an idle session is rejected and ended there); the
 * browser only keeps the clock in step so it can warn first and sign out
 * promptly. The last-activity stamp lives in localStorage so every tab of a
 * browser counts activity in any of them, and a tab opened later (or after a
 * browser restart) starts from the true figure.
 */

export const ACTIVITY_KEY = "hrms-last-activity";
export const HEARTBEAT_KEY = "hrms-last-heartbeat";
export const IDLE_SIGNOUT_KEY = "hrms-idle-signout";

/** The `details.reason` the API puts on a 401 caused by inactivity. */
export const IDLE_REASON = "idle_timeout";

export const IDLE_SIGN_OUT_MESSAGE = "You were signed out because of inactivity. Please sign in again.";

export type IdlePhase = "active" | "warning" | "expired";

export function remainingMs(nowMs: number, lastActivityMs: number, idleMs: number): number {
  return lastActivityMs + idleMs - nowMs;
}

/** Where the session stands: fine, inside the warning window, or timed out. */
export function idlePhase(nowMs: number, lastActivityMs: number, idleMs: number, warnMs: number): IdlePhase {
  const left = remainingMs(nowMs, lastActivityMs, idleMs);
  if (left <= 0) return "expired";
  if (left <= warnMs) return "warning";
  return "active";
}

/** How often activity is reported to the server: often enough that the server's
 * clock trails the browser's by little, rarely enough to be negligible load. */
export function heartbeatIntervalMs(idleMs: number): number {
  return Math.min(60_000, Math.max(2_000, Math.floor(idleMs / 10)));
}

/** 292000 -> "4:52" */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** "5 minutes", "1 minute", "30 seconds" for the warning copy. */
export function describeWindow(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds >= 60 && seconds % 60 === 0) {
    const minutes = seconds / 60;
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  return `${seconds} second${seconds === 1 ? "" : "s"}`;
}

export function readStamp(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function writeStamp(key: string, ms: number): void {
  try {
    localStorage.setItem(key, String(ms));
  } catch {
    // Storage blocked: this tab still tracks activity in memory, it just
    // cannot share it with other tabs.
  }
}

export function clearStamps(): void {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
    localStorage.removeItem(HEARTBEAT_KEY);
  } catch {
    // see writeStamp
  }
}

/** Tells the other tabs of this browser that the session ended for inactivity. */
export function announceIdleSignOut(): void {
  writeStamp(IDLE_SIGNOUT_KEY, Date.now());
}

/** Whether an error is the API saying the session ended for inactivity. */
export function isIdleTimeoutError(error: unknown): boolean {
  const body = axios.isAxiosError(error)
    ? (error.response?.data as { error?: { details?: { reason?: string } } } | undefined)
    : undefined;
  return body?.error?.details?.reason === IDLE_REASON;
}
